import { bianka, ReportActivityOptions } from '@fiverr-private/obs';
import { Click, ClickType, ElementType, Impression } from '@fiverr-private/bianka_js';
import { ClickedOnSendDocument } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/compliance/clicked_on_send_document';
import { ComplianceElement } from '@fiverr-private/bianka_js/protobuf/bianka/enums/compliance/compliance_element';
import { getContext } from '@fiverr-private/fiverr_context';

type Data = ClickedOnSendDocument;

export function reportClickEvent({
    eventName,
    elementName,
    sellerId,
    pageName,
    additionalData,
}: {
    eventName: string;
    elementName: string;
    pageName: string;
    sellerId?: number;
    additionalData?: ReportActivityOptions<Click, Data>['additionalData'];
}) {
    const { organizationId, userId, pageCtxId } = getContext();

    const eventToReport: ReportActivityOptions<Click, Data> = {
        type: Click,
        page: {
            name: pageName,
            ctxId: pageCtxId,
        },
        additionalData,
        event: {
            type: eventName,
            clickType: ClickType.UNSPECIFIED,
            organization: { id: organizationId ?? '' },
            element: { name: elementName, type: ElementType.UNSPECIFIED },
            ...(userId && { buyer: { id: userId } }),
            ...(sellerId && { seller: { id: sellerId } }),
        },
    };

    bianka.reportActivity(eventToReport);
}

export const reportImpressionEvent = ({
    eventName,
    elementName,
    pageName,
}: {
    eventName: string;
    elementName: string;
    pageName: string;
}) => {
    const { pageCtxId, userId, organizationId } = getContext();

    bianka.reportActivity<Impression>({
        type: Impression,
        page: {
            ctxId: pageCtxId,
            name: pageName,
        },
        event: {
            type: eventName,
            user: {
                id: userId as number,
            },
            component: {
                name: elementName,
                impId: '',
                type: '',
                level: 0,
                isLeaf: false,
                positionInParent: 0,
                sizeInParent: 0,
                visibleThroughScroll: false,
            },
            organization: {
                id: organizationId as string,
            },
        },
    });
};

export interface BiData {
    pageName: string;
}

export const reportEventsApi = {
    confirmRemoveDocument: ({ sellerId }: { sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_confirm_remove_document',
            elementName: 'confirm_remove_document',
            pageName: 'legal_documents',
            sellerId,
        });
    },
    requestUploadDocument: ({ pageName = '', sellerId }: { pageName?: string; sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_request_admin_via_email',
            elementName: 'request_admin_via_email',
            pageName,
            sellerId,
        });
    },
    sendAnotherDocument: ({ sellerId }: { sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_send_another_document',
            elementName: 'send_another_document',
            pageName: 'legal_documents',
            sellerId,
        });
    },
    previewDocument: () => {
        reportClickEvent({
            eventName: 'clicked_on_preview_documents',
            elementName: 'preview_documents',
            pageName: 'legal_documents',
        });
    },
    sendDocument: ({ pageName = '' }: BiData) => {
        reportClickEvent({
            eventName: 'clicked_on_send_document',
            elementName: 'send_document',
            pageName,
            additionalData: {
                type: ClickedOnSendDocument,
                message: {
                    complianceElement: ComplianceElement.SECTION,
                },
            },
        });
    },
    clickOnSelectDocuments: ({ pageName }: BiData) => {
        reportClickEvent({
            eventName: 'clicked_on_modal_request_leagal_docs',
            elementName: 'modal_request_leagal_docs',
            pageName,
        });
    },
    downloadDocument: () => {
        reportClickEvent({
            eventName: 'clicked_on_download_document',
            elementName: 'download_document',
            pageName: 'legal_documents',
        });
    },
    resendDocument: () => {
        reportClickEvent({
            eventName: 'clicked_on_resend_document',
            elementName: 'resend_document',
            pageName: 'legal_documents',
        });
    },
    removeDocument: () => {
        reportClickEvent({
            eventName: 'clicked_on_remove_document',
            elementName: 'remove_document',
            pageName: 'legal_documents',
        });
    },
    uploadDocument: ({ sellerId }: { sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_upload',
            elementName: 'upload',
            pageName: 'legal_documents',
            sellerId,
        });
    },
    viewedNoLegalDocs: ({ pageName }: { pageName: string }) => {
        reportImpressionEvent({
            eventName: 'viewed_no_legal_documents',
            elementName: 'no_legal_documents',
            pageName,
        });
    },
    viewedModalRequestLegalDocs: ({ pageName }: { pageName: string }) => {
        reportImpressionEvent({
            eventName: 'viewed_modal_request_legal_docs',
            elementName: 'modal_request_legal_docs',
            pageName,
        });
    },
};
